import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useSnackbar } from "notistack";
import { createContext, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { USER_ROLES_ARRAY } from "../constants";
import { useAuth } from "../features/auth/AuthContext";
import { useGetCurrentUserCargosQuery } from "../features/cargo/cargoApiSlice";

const POLLING_INTERVAL = 60_000; // 1 minute

const CargoTableContext = createContext();

export const CargoTableProvider = ({ children }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { user } = useAuth();

	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 50, // Number of rows per page
	});

	const [rowSelection, setRowSelection] = useState({});
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState("sale");
	const [shouldLockRefetch, setShouldLockRefetch] = useState(false);

	const onStatusChange = (newStatus) => {
		setStatus(newStatus);
		setRowSelection([]);
		setSearch("");
		setPagination({
			...pagination,
			pageIndex: 0,
		});
	};

	const {
		data: cargos,
		isLoading,
		isFetching,
		refetch,
	} = useGetCurrentUserCargosQuery(
		{
			status: status,
			page: pagination.pageIndex,
			perPage: pagination.pageSize,
			search: search,
		},
		{
			pollingInterval: shouldLockRefetch ? 0 : POLLING_INTERVAL,
			skipPollingIfUnfocused: true,
			skip: !user || !USER_ROLES_ARRAY.includes(user.role),
		}
	);

	const [prevCargos, setPrevCargos] = useState(null);
	const [cargosWithNewOffers, setCargosWithNewOffers] = useState([]);

	const checkNewOffers = (newCargos = cargos) => {
		if (prevCargos && newCargos && prevCargos.data && newCargos.data) {
			const newOffers = newCargos.data.reduce((acc, cargo) => {
				const prevCargo = prevCargos.data.find((pc) => pc.id === cargo.id);
				if (prevCargo && cargo.offers.length > prevCargo.offers.length) {
					acc.push(cargo.id);
					enqueueSnackbar(t("you_have_new_propositions_to_cargos"), {
						variant: "success",
						preventDuplicate: true,
						persist: true,
						action: (key) => {
							return (
								<IconButton
									onClick={() => closeSnackbar(key)}
									aria-label="close"
									color="primary"
									size="small"
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							);
						},
					});
				}
				return acc;
			}, []);

			setCargosWithNewOffers((prevState) => [
				...new Set([...prevState, ...newOffers]),
			]);
		}
	};

	useEffect(() => {
		if (!isLoading && isFetching) {
			setPrevCargos(cargos);
		}
	}, [isLoading, isFetching, cargos]);

	useEffect(() => {
		checkNewOffers();
	}, [cargos]);

	// useEffect(() => {
	// 	if (cargosWithNewOffers.length < 1) {
	// 		closeSnackbar();
	// 	}
	// }, [cargosWithNewOffers]);

	const handleRemoveSingle = (id) => {
		setCargosWithNewOffers((prevState) => prevState.filter((i) => i !== id));
	};

	const value = {
		cargos,
		isLoading,
		isFetching,
		refetch,
		pagination,
		setPagination,
		rowSelection,
		setRowSelection,
		search,
		setSearch,
		status,
		onStatusChange,
		newOffersIndexes: cargosWithNewOffers,
		handleRemoveSingleRedDot: handleRemoveSingle,
		setShouldLockRefetch,
	};

	return (
		<CargoTableContext.Provider value={value}>
			{children}
		</CargoTableContext.Provider>
	);
};

export const useCargosTable = () => useContext(CargoTableContext);
